import { APP_V2_APP_EVENTS, logError } from "../../lib/analytics";
import { isExpectedTokenExchangeError } from "../tokenExchangeError";

export type CognitoAuthFlow =
  | "magic-link-request"
  | "magic-link-verify"
  | "password"
  | "change-password"
  | "passkey";

export function mapCognitoError(
  error: unknown,
  flow: CognitoAuthFlow = "magic-link-verify"
): { code: string; message: string } {
  if (isExpectedTokenExchangeError(error)) {
    return {
      code: "auth/token-exchange-forbidden",
      message: "Sign in could not be completed. Please try again.",
    };
  }

  if (error && typeof error === "object" && "name" in error) {
    const amplifyError = error as { name: string; message: string };

    switch (amplifyError.name) {
      // When the DefineAuthChallenge Lambda throws an error (instead of returning
      // failAuthentication: true), Cognito surfaces it as a UserLambdaValidationException
      // with the Lambda's error message embedded. We use this to distinguish "user type
      // not allowed" from generic auth failures.
      case "UserLambdaValidationException": {
        if (amplifyError.message.includes("USER_TYPE_NOT_ALLOWED")) {
          return {
            code: "auth/user-type-not-allowed",
            message:
              "Your account type does not have access to this application. Please make sure you are using the correct app for your account.",
          };
        }
        if (amplifyError.message.includes("Unsupported custom challenge sub-type")) {
          return {
            code: "auth/token-exchange-forbidden",
            message: "Sign in could not be completed. Please try again.",
          };
        }
        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_UNEXPECTED_ERROR, {
          error,
          metadata: { errorName: amplifyError.name, errorMessage: amplifyError.message },
        });
        return {
          code: "auth/lambda-error",
          message: "An authentication error occurred.",
        };
      }
      case "UserNotFoundException": {
        return {
          code: "auth/user-not-found",
          message: "No registered user found for the provided email address.",
        };
      }
      // Cognito returns NotAuthorizedException with "Incorrect username or password" for:
      // - Magic-link request flow: user does not exist (CUSTOM_AUTH obscures existence)
      // - Magic-link verify flow: challenge response failed → expired/invalid link
      // - Password flow: wrong credentials entered by user
      case "NotAuthorizedException": {
        if (amplifyError.message.includes("Incorrect username or password")) {
          if (flow === "change-password") {
            return {
              code: "auth/incorrect-password",
              message: "Current password is incorrect. Please try again.",
            };
          }
          if (flow === "password") {
            return {
              code: "auth/incorrect-credentials",
              message: "Incorrect email or password. Please try again.",
            };
          }
          if (flow === "magic-link-request") {
            return {
              code: "auth/user-not-found",
              message: "No registered user found for the provided email address.",
            };
          }
          return {
            code: "auth/invalid-link",
            message:
              "This sign-in link has expired or has already been used. Links are valid for 10 minutes. Please request a new one.",
          };
        }
        if (flow === "change-password") {
          return {
            code: "auth/re-auth-required",
            message: "Your session has expired. Please sign in again and retry.",
          };
        }
        return {
          code: "auth/user-not-found",
          message: "No registered user found for the provided email address.",
        };
      }
      case "TooManyRequestsException": {
        return {
          code: "auth/too-many-requests",
          message: "Too many requests, please try again later",
        };
      }
      case "LimitExceededException": {
        return {
          code: "auth/limit-exceeded",
          message: "Attempt limit exceeded, please try after some time",
        };
      }
      case "CodeMismatchException": {
        return {
          code: "auth/invalid-code",
          message: "The reset code is invalid. Please request a new one.",
        };
      }
      case "ExpiredCodeException": {
        return {
          code: "auth/expired-code",
          message: "The reset code has expired. Please request a new one.",
        };
      }
      case "PasswordResetRequiredException": {
        if (flow === "password") {
          return {
            code: "auth/password-reset-required",
            message:
              'Your password needs to be reset. Please click "Forgot password?" below to set a new password.',
          };
        }
        return {
          code: "auth/password-reset-required",
          message:
            "Your password needs to be reset before you can sign in. Please go back to the login page and use the password login option to reset your password.",
        };
      }
      case "InvalidPasswordException": {
        return {
          code: "auth/invalid-password",
          message:
            "Password does not meet requirements. It must be at least 12 characters and include uppercase and lowercase letters.",
        };
      }
      case "NetworkError": {
        return { code: "auth/network-request-failed", message: "Network error occurred" };
      }
      // Browser WebAuthn ceremony errors (passkey flow). These surface as
      // DOMExceptions from navigator.credentials.{get,create}.
      case "NotAllowedError":
      case "AbortError": {
        return {
          code: "auth/passkey-cancelled",
          message: "Passkey prompt was dismissed or timed out. Please try again.",
        };
      }
      case "InvalidStateError": {
        return {
          code: "auth/passkey-already-registered",
          message: "A passkey is already registered on this device for your account.",
        };
      }
      case "SecurityError": {
        return {
          code: "auth/passkey-security-error",
          message:
            "Passkeys can't be used on this page. Please make sure you are on a supported, secure domain.",
        };
      }
      case "NotSupportedError": {
        return {
          code: "auth/passkey-not-supported",
          message: "Passkeys are not supported on this device or browser.",
        };
      }
      default: {
        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_UNEXPECTED_ERROR, {
          error,
          metadata: { errorName: amplifyError.name, errorMessage: amplifyError.message },
        });
        return {
          code: "auth/unknown",
          message: amplifyError.message || "An unknown error occurred",
        };
      }
    }
  }

  logError(APP_V2_APP_EVENTS.COGNITO_AUTH_UNEXPECTED_ERROR, {
    error: error instanceof Error ? error : new Error(String(error)),
    metadata: { errorMessage: String(error) },
  });
  return { code: "auth/unknown", message: "An unknown error occurred" };
}
